import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';

import { getTodos, createTodo, updateTodo, deleteTodo, promoteTodo } from '@/services/taskService';
import { todayYMD } from '@/utils/taskDates';
import { IGNORE_PW_MANAGERS } from '@/utils/formHints';

/**
 * Personal to-do list (private to the logged-in staff member).
 * Items can be ticked off (→ collapsed Done section) or promoted onto the
 * weekly board for a chosen day.
 */
const PersonalTodoList = ({ onPromoted }) => {
  const [todos, setTodos]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [text, setText]         = useState('');
  const [showDone, setShowDone] = useState(false);
  const [promoting, setPromoting]     = useState(null);          // todo id being scheduled
  const [promoteDate, setPromoteDate] = useState(todayYMD());    // chosen day for promotion
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [promotePos, setPromotePos]   = useState(null);          // { top, left } for the portal popover
  const popoverRef = useRef(null);

  const openPromote = (id, e) => {
    if (promoting === id) { setPromoting(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const width = 208; // w-52
    // Anchor below the icon, keep within the viewport, flip up if no room below.
    let left = Math.min(rect.right - width, window.innerWidth - width - 8);
    left = Math.max(8, left);
    const below = window.innerHeight - rect.bottom;
    const top = below < 170 ? rect.top - 150 : rect.bottom + 6;
    setPromotePos({ top, left });
    setPromoteDate(todayYMD());
    setPromoting(id);
  };

  // Close the promote popover on outside click or scroll.
  useEffect(() => {
    if (promoting == null) return;
    const onDown = (ev) => { if (popoverRef.current && !popoverRef.current.contains(ev.target)) setPromoting(null); };
    const onScroll = () => setPromoting(null);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('scroll', onScroll, true); };
  }, [promoting]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTodos(await getTodos() || []); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText('');
    try { await createTodo(t); await load(); } catch (e) { console.error(e); }
  };

  const handleToggle = async (todo) => {
    setTodos((prev) => prev.map((x) => x.id === todo.id ? { ...x, is_done: !x.is_done } : x));
    try { await updateTodo(todo.id, { is_done: !todo.is_done }); } catch { load(); }
  };

  const handleDelete = async (id) => {
    setTodos((prev) => prev.filter((x) => x.id !== id));
    try { await deleteTodo(id); } catch { load(); }
  };

  const handlePromote = async (id) => {
    if (!promoteDate) return;
    setPromoteBusy(true);
    try {
      await promoteTodo(id, { due_date: promoteDate });
      setPromoting(null);
      await load();
      onPromoted && onPromoted();
    } catch (e) { console.error(e); }
    setPromoteBusy(false);
  };

  const open = todos.filter((t) => !t.is_done);
  const done = todos.filter((t) => t.is_done);

  return (
    <div className="flex flex-col min-h-0">
      <div className="flex items-center gap-1.5 px-1 mb-2">
        <ChecklistRtlIcon style={{ fontSize: 16 }} className="text-[#002169]" />
        <h3 className="text-sm font-bold text-[#002169]">My To-Do</h3>
        {open.length > 0 && <span className="text-[10px] font-bold bg-[#002169] text-white rounded-full px-1.5 py-0.5">{open.length}</span>}
      </div>

      {/* Add */}
      <form onSubmit={handleAdd} className="flex items-center gap-1 mb-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a to-do…"
          {...IGNORE_PW_MANAGERS}
          className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
        <button type="submit" className="flex-shrink-0 bg-[#002169] text-white rounded-lg p-1.5 hover:bg-[#001244] transition">
          <AddIcon style={{ fontSize: 16 }} />
        </button>
      </form>

      {/* Open items */}
      <div className="space-y-1 overflow-y-auto">
        {loading ? (
          <p className="text-xs text-gray-400 italic px-1 py-2">Loading…</p>
        ) : open.length === 0 ? (
          <p className="text-xs text-gray-400 italic px-1 py-2">Nothing on your list. Nice.</p>
        ) : open.map((t) => (
          <div key={t.id} className="group relative flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition">
            <input type="checkbox" checked={false} onChange={() => handleToggle(t)}
              className="mt-0.5 flex-shrink-0 w-4 h-4 rounded border-gray-300 text-[#002169] focus:ring-blue-300 cursor-pointer" />
            <span className="flex-1 text-sm text-gray-700 break-words">{t.text}</span>
            <div className={`flex items-center gap-0.5 transition flex-shrink-0 ${promoting === t.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
              <button title="Add to weekly board" onClick={(e) => openPromote(t.id, e)}
                className={`hover:text-blue-600 ${promoting === t.id ? 'text-blue-600' : 'text-gray-400'}`}><EventAvailableIcon style={{ fontSize: 15 }} /></button>
              <button title="Delete" onClick={() => handleDelete(t.id)}
                className="text-gray-400 hover:text-red-600"><DeleteOutlineIcon style={{ fontSize: 15 }} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Done section */}
      {done.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <button onClick={() => setShowDone((v) => !v)}
            className="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-gray-600 transition">
            {showDone ? <ExpandMoreIcon style={{ fontSize: 16 }} /> : <ChevronRightIcon style={{ fontSize: 16 }} />}
            Done ({done.length})
          </button>
          {showDone && (
            <div className="space-y-1 mt-1">
              {done.map((t) => (
                <div key={t.id} className="group flex items-start gap-2 px-2 py-1 rounded-lg hover:bg-gray-50 transition">
                  <input type="checkbox" checked readOnly onChange={() => handleToggle(t)}
                    className="mt-0.5 flex-shrink-0 w-4 h-4 rounded text-gray-400 cursor-pointer" />
                  <span className="flex-1 text-sm text-gray-400 line-through break-words">{t.text}</span>
                  <button title="Delete" onClick={() => handleDelete(t.id)}
                    className="text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
                    <DeleteOutlineIcon style={{ fontSize: 15 }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Promote popover — portalled to <body> so it floats above the sidebar's
          scroll container instead of being clipped by it. */}
      {promoting != null && promotePos && createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[2000] w-52 bg-white border border-gray-200 rounded-xl shadow-2xl p-2.5"
          style={{ top: promotePos.top, left: promotePos.left }}
        >
          <p className="text-[11px] font-semibold text-gray-500 mb-1.5">Add to board on:</p>
          <input type="date" value={promoteDate} autoFocus onChange={(e) => setPromoteDate(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 mb-2" />
          <div className="flex items-center gap-1.5">
            <button onClick={() => handlePromote(promoting)} disabled={promoteBusy || !promoteDate}
              className="flex-1 bg-[#002169] text-white text-xs font-bold rounded-lg py-1.5 hover:bg-[#001244] disabled:opacity-50 transition">
              {promoteBusy ? 'Adding…' : 'Add to board'}
            </button>
            <button onClick={() => setPromoting(null)}
              className="px-2.5 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition">Cancel</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PersonalTodoList;
